import { useEffect, useState, useMemo, useRef } from "react";
// import { logos } from "tokens/build/icons/dist";
import camelCase from "lodash-es/camelCase";
import { CollegeBasketball } from "./components/CollegeBasketball";
import { CollegeFootball } from "./components/CollegeFootball";
import { normalizeSport } from "shared/src/utils/sportMap";
import { CollegeBaseball } from "./components/CollegeBaseball";
import { CollegeHockey } from "./components/CollegeHockey";
import { Soccer, SoccerJerseyType } from "./components/Soccer";
import {
  TeamLogoProps,
  TeamLogoIconWrapperProps,
  TeamLogoModuleType,
} from "./types";
import { useTeamColors } from "shared/src/hooks/useTeamColors";
import { useCompetitionType } from "shared/src/hooks/useCompetitionType";
import { sizeStyles } from "shared/src/styles/sizeStyles";
import styles from "./TeamLogo.module.scss";
import startCase from "lodash-es/startCase";
import { Image } from "components/src/components/Image/Image";

const IconWrapper = ({
  children,
  size,
  className = "",
  style,
  flipIcon,
  rest,
}: TeamLogoIconWrapperProps) => {
  const { sizeClasses } = sizeStyles(size);
  return (
    <i
      className={`${sizeClasses} ${className} cui__color_icon ${flipIcon ? "-cui-scale-x-100" : ""}`}
      style={style}
      {...rest}
    >
      {children}
    </i>
  );
};

function getLogoName(abbr: any, mode: string) {
  return `logo_${abbr.toLowerCase()}_${mode === "dark" ? "onDark" : "onLight"}`;
}

const customLogos = ["college", "football", "soccer"];

export const TeamLogo = ({
  name = "",
  size = "md",
  sport: sportProp = "basketball",
  mode = "light",
  soccerType = "stripeVertical",
  accessibilityLabel = "",
  flipIcon,
  className = "",
  league: leagueProp,
  competition: competitionProp,
  competitionType,
  type,
  isPlaceholder,
  ...rest
}: TeamLogoProps) => {
  const { sport: _sportRaw, league } = useCompetitionType({ sport: sportProp, league: leagueProp, competition: competitionProp, competitionType });
  const sport = normalizeSport(_sportRaw);
  const { sizeMap, sizeStyle } = sizeStyles(size);

  // TODO: Get rid of special cases for 'rugby' - make logic generic OR fix teams data so it doesn't matter
  const sportIsRugbyType = sport && sport?.includes("rugby");
  const limitTo = competitionType !== undefined
    ? { limitTo: [String(competitionType)] }
    : sport === "football" || sportIsRugbyType
      ? { limitTo: sportIsRugbyType ? ["rugby"] : ["football", "soccer"] }
      : undefined;
  const {
    colors,
    name: foundTeamName,
    league: foundLeague,
    abbreviation: abbr,
  } = useTeamColors(sportIsRugbyType ? `rugby-${name}` : name, limitTo);

  const [primary = "", secondary = "", jerseyType] = colors || [];

  const _league = foundLeague ? foundLeague : league;

  let _name =
    _league && !customLogos.includes(_league) && abbr
      ? getLogoName(abbr, mode).replace("-", "_")
      : undefined;

  if (type === "helmet" && _league === "nfl") {
    _name = _name?.replace(/_(onLight|onDark)$/, "");
    _name = `${_name}__helmet_${flipIcon ? "facingLeft" : "facingRight"}`;
  }

  const isCollege = _league === "college" || (typeof competitionType === "string" && competitionType?.includes("college"));
  const isSoccer = ((_league === "soccer" || _league === "football" || isPlaceholder) && sport && sport === "football") || competitionType === "soccer" || competitionType === "football";
  const isRugby = _league === "rugby" || sport?.includes("rugby") || competitionType === "rugby";

  const width =
    typeof size === "number"
      ? sizeStyle.width?.replace("px", "")
      : sizeMap.width;
  const height =
    typeof size === "number"
      ? sizeStyle.height?.replace("px", "")
      : sizeMap.height;

  const loadingStyles = { minWidth: width, minHeight: height };
  const _flipIcon = isCollege && flipIcon;

  const iconWrapperProps = useMemo(() => {
    return {
      size,
      flipIcon: _flipIcon,
      rest,
      style: loadingStyles,
      className,
    };
  }, [size, _flipIcon, rest, loadingStyles, className]);

  if (isSoccer || isRugby) {
    return (
      <IconWrapper
        {...iconWrapperProps}
        className={`${className}`}
      >
        <Soccer
          type={jerseyType as SoccerJerseyType ?? "plain"}
          primaryColor={primary ?? "fg-moderate"}
          secondaryColor={secondary ?? "fg-subtle"}
          width={sizeMap.width}
          height={sizeMap.height}
          accessibilityLabel={`${startCase(name)} logo`}
        />
      </IconWrapper>
    );
  }
  // ncaaf: football
  if (isCollege || _league === "wnba") {
    switch (true) {
      case [
        "basketball",
        "ncaab",
        "ncaawb",
        "collegeBasketball",
        "college_basketball",
      ].includes(sport ?? ""):
        return (
          <IconWrapper {...iconWrapperProps}>
            <CollegeBasketball
              fill={primary || "var(--fg-moderate)"}
              secondaryfill={secondary || "var(--fg-subtle)"}
              width={sizeMap.width}
              height={sizeMap.height}
              accessibilityLabel={`${startCase(name)} logo`}
            />
          </IconWrapper>
        );
      case [
        "americanfootball",
        "ncaaf",
        "collegeFootball",
        "college_football",
      ].includes(sport ?? ""):
        return (
          <IconWrapper {...iconWrapperProps}>
            <CollegeFootball
              fill={primary || "var(--fg-moderate)"}
              secondaryfill={secondary || "var(--fg-subtle)"}
              width={sizeMap.width}
              height={sizeMap.height}
              accessibilityLabel={`${startCase(name)} logo`}
            />
          </IconWrapper>
        );
      case [
        "baseball",
        "ncaabaseball",
        "dibaseball",
        "di baseball",
        "collegeBaseball",
        "college_baseball",
      ].includes(sport ?? ""):
        return (
          <IconWrapper {...iconWrapperProps}>
            <CollegeBaseball
              fill={primary || "var(--fg-moderate)"}
              secondaryfill={secondary || "var(--fg-subtle)"}
              width={sizeMap.width}
              height={sizeMap.height}
              accessibilityLabel={`${startCase(name)} logo`}
            />
          </IconWrapper>
        );
      case ["hockey", "ncaah", "icehockey", "collegeHockey", "college_hockey"].includes(
        sport ?? ""
      ):
        return (
          <IconWrapper {...iconWrapperProps}>
            <CollegeHockey
              fill={primary || "var(--fg-moderate)"}
              secondaryfill={secondary || "var(--fg-subtle)"}
              width={sizeMap.width}
              height={sizeMap.height}
              accessibilityLabel={`${startCase(name)} logo`}
            />
          </IconWrapper>
        );
      case ["basketball", "ncaam", "ncaaw"].includes(sport ?? ""):
        return (
          <IconWrapper {...iconWrapperProps}>
            <CollegeBasketball
              fill={primary || "var(--fg-moderate)"}
              secondaryfill={secondary || "var(--fg-subtle)"}
              width={sizeMap.width}
              height={sizeMap.height}
              accessibilityLabel={`${startCase(name)} logo`}
            />
          </IconWrapper>
        );
      default:
        return null;
    }
  }

  return (
    <Image
      name={_name}
      size={size}
      className={`${_flipIcon ? "-cui-scale-x-100" : ""} ${className}`.trim()}
      {...rest}
      accessibilityLabel={accessibilityLabel}
    />
  );
};

TeamLogo.displayName = "TeamLogo";
TeamLogo.isImageType = true;
